import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { RootState, AppDispatch } from '../store'
import { acceptInvitation, declineInvitation } from '../store/invitationsSlice'
import { setRooms } from '../store/roomsSlice'
import {
  Invitation,
  InvitationStatus,
  invitationTargetName,
  isInvitationRecipient,
} from '../types/invitation'
import roomsService from '../services/roomsService'
import { getCitiesByCountry, getCountries } from '../services/publicService'
import type { CityItem, CountryItem } from '../types/public'
import SearchableSelect from './SearchableSelect'
import { useTranslation } from '../hooks/use-i18n'
import toast from 'react-hot-toast'

interface InvitationCardProps {
  invitation: Invitation
}

const InvitationCard: React.FC<InvitationCardProps> = ({ invitation }) => {
  const dispatch = useDispatch<AppDispatch>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useSelector((state: RootState) => state.auth)
  const [isLoading, setIsLoading] = useState(false)
  const [countryCode, setCountryCode] = useState('')
  const [city, setCity] = useState('')
  const [countries, setCountries] = useState<CountryItem[]>([])
  const [cities, setCities] = useState<CityItem[]>([])
  const [loadingCities, setLoadingCities] = useState(false)

  const isRecipient = isInvitationRecipient(invitation, user)
  const canRespond = invitation.status === InvitationStatus.Pending && isRecipient
  const isWorld = invitation.kind === 'world_football' || invitation.kind === 'world_formula1'
  const needsLocation = isWorld && invitation.requiresLocation !== false
  const targetName = invitationTargetName(invitation, t)

  useEffect(() => {
    if (!canRespond || !needsLocation) return
    getCountries().then(setCountries).catch(() => setCountries([]))
  }, [canRespond, needsLocation])

  useEffect(() => {
    if (!countryCode.trim()) {
      setCities([])
      setCity('')
      return
    }
    let cancelled = false
    setLoadingCities(true)
    setCity('')
    getCitiesByCountry(countryCode)
      .then((list) => { if (!cancelled) setCities(list) })
      .catch(() => { if (!cancelled) setCities([]) })
      .finally(() => { if (!cancelled) setLoadingCities(false) })
    return () => { cancelled = true }
  }, [countryCode])

  const handleAccept = async () => {
    if (needsLocation && (!countryCode.trim() || !city.trim())) {
      toast.error(t('invitations.chooseLocation'))
      return
    }
    setIsLoading(true)
    try {
      await dispatch(acceptInvitation({
        invitationId: invitation._id,
        countryCode: needsLocation ? countryCode : undefined,
        city: needsLocation ? city : undefined,
      })).unwrap()
      if (!isWorld) {
        await queryClient.invalidateQueries({ queryKey: ['user-rooms'] })
        const freshRooms = await roomsService.getUserRooms()
        queryClient.setQueryData(['user-rooms'], freshRooms)
        dispatch(setRooms(freshRooms))
      }
      toast.success(t('invitations.accepted'))
      if (invitation.kind === 'world_formula1') navigate('/public/formula1')
      else if (invitation.kind === 'world_football') navigate('/public/football')
      else navigate('/')
    } catch (error: any) {
      toast.error(error.message || t('invitations.acceptFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleDecline = async () => {
    setIsLoading(true)
    try {
      await dispatch(declineInvitation(invitation._id)).unwrap()
      toast.success(t('invitations.declined'))
    } catch (error: any) {
      toast.error(error.message || t('invitations.declineFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: InvitationStatus) => {
    switch (status) {
      case InvitationStatus.Pending:
        return 'text-yellow-400'
      case InvitationStatus.Accepted:
        return 'text-green-400'
      case InvitationStatus.Rejected:
        return 'text-red-400'
      default:
        return 'text-white/60'
    }
  }

  const getStatusText = (status: InvitationStatus) => {
    switch (status) {
      case InvitationStatus.Pending:
        return t('invitations.statusPending')
      case InvitationStatus.Accepted:
        return t('invitations.statusAccepted')
      case InvitationStatus.Rejected:
        return t('invitations.statusRejected')
      default:
        return status
    }
  }

  return (
    <div className="blue-sky-card rounded-2xl p-6 mb-4 transform hover:scale-105 transition-all duration-300">
      <div className="text-center">
        <h3 className="text-lg sm:text-xl font-semibold text-white mb-4">
          {isRecipient
            ? t('invitations.receivedCopy', { sender: invitation.sender.name, target: targetName })
            : t('invitations.sentCopy', {
              recipient: invitation.recipient.name || invitation.recipient.email || invitation.recipientEmail || '',
              target: targetName,
            })
          }
          {invitation.role && (
            <span className="text-sky-400"> {t('invitations.asRole', { role: invitation.role })}</span>
          )}
        </h3>

        <div className="mb-4">
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invitation.status)} bg-white/10`}>
            {getStatusText(invitation.status)}
          </span>
        </div>

        {canRespond && needsLocation && (
          <div className="text-left max-w-sm mx-auto mb-4 space-y-3">
            <p className="text-sm text-white/80 text-center">{t('invitations.chooseLocation')}</p>
            <SearchableSelect
              items={countries.map((c) => ({ value: c.code, label: c.name }))}
              value={countryCode}
              onChange={setCountryCode}
              placeholder={t('playPublic.selectCountry')}
            />
            {loadingCities ? (
              <p className="text-sm text-white/60">{t('common.loading')}</p>
            ) : (
              <SearchableSelect
                items={cities.map((c) => ({ value: c.cityName, label: c.cityName }))}
                value={city}
                onChange={setCity}
                disabled={!countryCode}
                placeholder={countryCode ? t('playPublic.selectCity') : t('playPublic.selectCountryFirst')}
              />
            )}
          </div>
        )}

        {canRespond && (
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleAccept}
              disabled={isLoading || (needsLocation && (!countryCode.trim() || !city.trim()))}
              className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl font-semibold"
            >
              {isLoading ? t('invitations.processing') : t('invitations.accept')}
            </button>
            <button
              onClick={handleDecline}
              disabled={isLoading}
              className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl font-semibold"
            >
              {isLoading ? t('invitations.processing') : t('invitations.decline')}
            </button>
          </div>
        )}

        {!canRespond && (
          <div className="text-white/60 text-sm">
            {isRecipient
              ? t('invitations.alreadyResponded')
              : t('invitations.waitingForResponse')
            }
          </div>
        )}
      </div>
    </div>
  )
}

export default InvitationCard
